export type ValidationTestStatus = 'passed' | 'warning' | 'failed' | 'not_run'

export interface ValidationTestResult {
  id?: string
  check_id: string
  category: string
  label: string
  status: ValidationTestStatus
  duration_ms: number
  evidence?: Record<string, unknown>
  remediation?: string | null
}

export function groupValidationResults(results: ValidationTestResult[]) {
  const grouped = new Map<string, ValidationTestResult[]>()
  for (const result of results) {
    grouped.set(result.category, [...(grouped.get(result.category) || []), result])
  }
  return Array.from(grouped.entries())
}

export type ValidationRunMode = 'local' | 'sample' | 'credentialed'
export type ExternalValidationScope = 'saviynt_authentication' | 'full_connection'

export function isFileValidationFamily(connectorFamily?: string | null) {
  return ['FILE', 'CSV', 'IDENTITY', 'SCHEMAUSERJOB', 'SCHEMA_USER_IMPORT']
    .includes((connectorFamily || '').trim().toUpperCase())
}

export interface ComparableValidationRun {
  id: string
  status: 'passed' | 'warning' | 'failed'
  counts: Record<ValidationTestStatus, number>
  duration_ms: number
  run_mode?: ValidationRunMode
  external_scope?: ExternalValidationScope | null
  created_at: string
  results?: ValidationTestResult[]
}

export interface ValidationStatusChange {
  check_id: string
  label: string
  category: string
  before: ValidationTestStatus
  after: ValidationTestStatus
  direction: 'improved' | 'regressed' | 'changed'
}

export function validationRunTypeLabel(
  mode: ValidationRunMode = 'local',
  scope?: ExternalValidationScope | null,
) {
  if (mode === 'sample') return 'Sample'
  if (mode === 'credentialed' && scope === 'saviynt_authentication') return 'Saviynt authentication'
  if (mode === 'credentialed') return 'Full connection'
  return 'Local'
}

const validationStatusRank: Record<ValidationTestStatus, number> = {
  failed: 0,
  not_run: 1,
  warning: 2,
  passed: 3,
}

export function compareValidationResults(
  beforeResults: ValidationTestResult[],
  afterResults: ValidationTestResult[],
) {
  const beforeById = new Map(beforeResults.map(result => [result.check_id, result]))
  const afterById = new Map(afterResults.map(result => [result.check_id, result]))
  const changes: ValidationStatusChange[] = []
  let unchanged = 0

  const checkIds = Array.from(beforeById.keys()).concat(Array.from(afterById.keys()))
  for (const checkId of Array.from(new Set(checkIds)).sort()) {
    const before = beforeById.get(checkId)
    const after = afterById.get(checkId)
    const beforeStatus = before?.status || 'not_run'
    const afterStatus = after?.status || 'not_run'
    if (beforeStatus === afterStatus) {
      unchanged += 1
      continue
    }
    const beforeRank = validationStatusRank[beforeStatus]
    const afterRank = validationStatusRank[afterStatus]
    changes.push({
      check_id: checkId,
      label: after?.label || before?.label || checkId,
      category: after?.category || before?.category || 'unknown',
      before: beforeStatus,
      after: afterStatus,
      direction: afterRank > beforeRank ? 'improved' : afterRank < beforeRank ? 'regressed' : 'changed',
    })
  }

  return {
    changes,
    unchanged,
    improved: changes.filter(change => change.direction === 'improved').length,
    regressed: changes.filter(change => change.direction === 'regressed').length,
  }
}

export function validationRunHeadline(status: 'passed' | 'warning' | 'failed', mode: ValidationRunMode = 'local') {
  if (status === 'failed') return 'Validation failed'
  if (status === 'warning') return 'Validation completed with warnings'
  if (mode === 'credentialed') return 'Authorized connection test passed'
  if (mode === 'sample') return 'Sample validation passed'
  return 'Local validation passed'
}

export function buildValidationRunRequest(
  sampleText: string,
  external: boolean,
  externalScope: ExternalValidationScope = 'full_connection',
  connectorFamily?: string | null,
) {
  let sampleResponse: unknown = null
  if (sampleText.trim()) {
    const trimmedSample = sampleText.trim()
    try {
      sampleResponse = JSON.parse(trimmedSample)
    } catch {
      if (trimmedSample.startsWith('<')) sampleResponse = trimmedSample
      else if (isFileValidationFamily(connectorFamily) && /[\n\r]/.test(trimmedSample) && /[,|\t;~]/.test(trimmedSample.split(/\r?\n/, 1)[0])) {
        sampleResponse = trimmedSample
      } else throw new Error('The sample response must be valid JSON, SOAP XML, or CSV for a file-based import.')
    }
  }
  return {
    sample_response: sampleResponse,
    external_authorization: external ? 'contact_configured_systems' : null,
    external_scope: external ? externalScope : null,
  }
}
